import { Observable } from "rxjs";
import * as jp from 'jsonpath';
export abstract class Geometry<T> implements IGeometry {
    hasM: boolean;
    hasZ: boolean;
    type: GeometryType;
    geodesic: boolean;
    title: string;

    constructor(obj?: any, context?: any) {
        if (context)
            this.createFromContext(obj, context);
        else {
            this.title = obj.title || this.type;
            this.geodesic = obj.geodesic || false;
            this.hasM = obj.hasM || false;
            this.hasZ = obj.hasZ || false;
        }
    }

    abstract createFromContext(config: T, context: any);
    abstract geom(Graphic, GeometryEngine);
}

export interface IGeometry {
    hasM: boolean,
    hasZ: boolean,
    type: GeometryType,
    geodesic: boolean,
    title: string,
}

export interface Point extends IGeometry {
    x: number;
    y: number;
    z: number;
}

export interface Polyline extends IGeometry {
    paths: number[][][];
}

export interface Polygon extends IGeometry {
    rings: number[][][];
}

export class GISPoint extends Geometry<PointConfig> implements Point {

    readonly type = GeometryType.Point;
    bufferFt: number;
    x;
    y;
    z;

    constructor(obj: any = {}, context = null) {
        super(obj, context);

        if (!context) {
            this.x = obj.x;
            this.y = obj.y;
            this.z = obj.z;
            this.hasZ = !!this.z;
            this.geodesic = obj.geodesic || false;
            this.bufferFt = obj.bufferFt || 0;
            this.title = obj.title || this.type;
        }
    }

    createFromContext(config: PointConfig, context: any) {
        this.x = Number(jp.value(context, config.xContextLocation) || undefined);
        this.y = Number(jp.value(context, config.yContextLocation) || undefined);

        if (isNaN(this.x))
            this.x = null;
        if (isNaN(this.y))
            this.y = null;

        try {
            this.z = Number(jp.value(context, config.zContextLocation) || undefined);

            if (isNaN(this.z))
                this.z = null;
        } catch (ex) { }

        this.hasZ = !!this.z;
    }

    geom(Graphic: any, GeometryEngine: any) {
        if (!this.x || !this.y)
            return null;

        let geom = new Graphic({
            geometry: {
                type: this.type,
                x: this.x,
                y: this.y,
                z: this.z,
                hasZ: !!this.z
            },
            symbol: {
                type: "simple-marker",
                color: [0, 0, 0]
            }
        });

        if (this.bufferFt && this.bufferFt !== 0)
            return new Graphic({
                geometry: GeometryEngine.geodesicBuffer(geom.geometry, this.bufferFt, 'feet'),
                symbol: {
                    type: "simple-fill",
                    color: [0, 0, 0],
                    outline: {
                        color: [128, 128, 128, 0.5],
                        width: "0.5px"
                    }
                }
            });
        else
            return geom;
    }
}

export class GISPolyline extends Geometry<PolylineConfig> implements Polyline {

    readonly type = GeometryType.polyline;
    paths: number[][][];

    constructor(obj: any = {}, context = null) {
        super(obj, context);

        if (!context) {
            this.paths = obj.paths || [];
            this.title = obj.title || this.type;
        }
    }

    createFromContext(config: PolylineConfig, context: any) {
        let path: number[][] = [];
        config.mappings.forEach(x => {
            try {
                //ensure to set to undefined. Number(null) === 0 === true
                let long = Number(jp.value(context, x.xContextLocation) || undefined);
                let lat = Number(jp.value(context, x.yContextLocation) || undefined);
                try {
                    let z = Number(jp.value(context, x.zContextLocation) || undefined);
                    if (!isNaN(long) && !isNaN(lat) && !isNaN(z))
                        path.push([long, lat, z]);
                } catch (ex) {
                    if (!isNaN(long) && !isNaN(lat))
                        path.push([long, lat]);
                }
            } catch (ex) { }
        });
        this.paths = [path];
    }

    geom(Graphic: any, GeometryEngine: any) {
        return new Graphic({
            geometry: {
                type: this.type,
                paths: this.paths
            },
            symbol: {
                type: "simple-line",
                color: [0, 0, 0],
                width: 4
            }
        });
    }
}

export class GISPolygon extends Geometry<any> implements Polygon {
    readonly type = GeometryType.polygon;
    rings: number[][][] = [];

    geom() {
        throw new Error("Method not implemented.");
    }

    createFromContext(config: PolylineConfig, context: any) {
        throw new Error("Method not implemented.");
    }
}

export enum GeometryType {
    Point = 'point',
    polyline = 'polyline',
    polygon = 'polygon',
}

export interface ConfigurationComponent<T> {
    model: T;
    saveChange: Observable<any>;
}

export interface PointConfig extends IGeometry {
    title: string;
    xContextLocation: string;
    yContextLocation: string;
    zContextLocation: string;
    bufferFt: number;
}

export interface PolylineConfig extends IGeometry {
    mappings: { xContextLocation: string, yContextLocation: string, zContextLocation?: string }[];
}